import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { UserService, UserInfo } from '../../services/user/user.service';
import { AwardsService, AwardProgramInfo } from '../../services/awards/awards.service';
import { ErrorService } from '../../services/error/error.service';
import { ApplicationService, JudgeNominationInfo, NomineeTypeInfo, JudgeNominationInfoEntry, NominationWindowInfo, JudgeInfo } from '../../services/application/application.service';
import { ScrollService } from '../../services/scroll/scroll.service';
import { MatDialog } from '@angular/material/dialog';
import { JudgeSelectDialogComponent } from '../../services/dialogs/judgeselect/judgeselect.dialogcomponent';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
    selector: 'app-judgenominees',
    templateUrl: './judgenominees.component.html',
    styleUrls: ['./judgenominees.component.scss']
})

export class JudgeNomineesComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();
    User: UserInfo;
    Award: AwardProgramInfo;
    Judge: JudgeNominationInfo;
    NomType: NomineeTypeInfo;
    TieForm: FormGroup;
    Window: NominationWindowInfo;
    AvailableRanks: number[];
    Phase: string;
    DuplicateRank: number;
    ViewReady: boolean;
    NoNominees: boolean;
    IsJudge: boolean;
    NoJudgingWindow: boolean;
    AllJudges: JudgeInfo[];
    AllNomTypes: NomineeTypeInfo[];
    AllWindows: NominationWindowInfo[];
	CurrentJudge: string;
	SchoolYear: string;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private matDialog: MatDialog,
        private errSvc: ErrorService,
        private fb: FormBuilder,
        private usrSvc: UserService,
        private router: Router,
		private scrollSvc: ScrollService,
		private settingsSvc: SettingsService) { }

    ngOnInit() {
        this.User = this.usrSvc.User;
        this.Award = this.awardSvc.CurrentAwards;
		this.AvailableRanks = [];

		if (this.User == null) return;

		this.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
			this.SchoolYear = year;

			let nomTypes$ = this.appSvc.GetNomineeTypes();
			let windows$ = this.appSvc.GetNominationWindows(year);

			combineLatest(nomTypes$, windows$).pipe(
				takeUntil(this._subrelease),
				map(_pck => {
					return {
						nomTypes: _pck[0],
						windows: _pck[1]
					};
				})
			).subscribe(packet => {
				this.AllNomTypes = packet.nomTypes;
				this.AllWindows = packet.windows;

				if (this.AllWindows.some(s => s.JudgingPhase > 0)) {
					this.NoJudgingWindow = false;
				}
				else {
					this.NoJudgingWindow = true;
				}

				if (this.User.CurrentRole === 'SystemAdmin' || this.User.CurrentRole === 'Developer') {
					this.IsJudge = false;
					this.AdminInit();
				}
				else {
					this.IsJudge = true;
					this.JudgeInit(null);
				}
			}, error => this.errSvc.HandleServiceError(error));
		});
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

	JudgeInit(judgeID: number) {
		this.appSvc.GetJudgeNominees(this.User.AuthToken, this.User.CurrentRole, this.Award.AwardID, judgeID, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(judge => {
            this.Judge = judge;

            if (judge.JudgingPhase != null) {
                this.Window = this.AllWindows.find(f => f.AwardID === this.Award.AwardID && f.JudgingPhase === judge.JudgingPhase)
            }

            if (judge.Nominees == null) {
                this.NoNominees = true;
                return;
            }

            this.NoNominees = false;
            this.NomType = this.AllNomTypes.find(f => f.NomineeTypeID === judge.NomineeTypeID);

            switch (judge.JudgingPhase) {
                case 1: {
                    this.Phase = 'Initial Judging';
                    break;
                }
                case 2: {
                    this.Phase = 'Initial Judging Tie-Breaker';
                    break;
                }
                case 3: {
                    this.Phase = 'Final Judging';
                    break;
                }
                case 4: {
                    this.Phase = 'Final Judging Tie-Breaker';
                    break;
                }
                default: {
                    this.Phase = '';
                    break;
                }
            }

            for (let i = 1; i <= judge.Nominees.length; i++) {
                this.AvailableRanks.push(i);
            }

            this.TieForm = this.fb.group({
                RankingsList: this.fb.array([])
            });

            let rankingsArray = this.TieForm.get('RankingsList') as FormArray;
            for (let nom of judge.Nominees) {
                rankingsArray.push(this.CreateRankingDetailFormGroup(nom));
                nom.JudgingPhase = judge.JudgingPhase;
            }

            rankingsArray.valueChanges.pipe(takeUntil(this._subrelease)).subscribe((vals: any[]) => {
                let ranks = vals.map(m => m.Rank);
                this.DuplicateRank = ranks.find((v, i) => ranks.findIndex(x => x === v) !== i);
            });

            this.ViewReady = true;
        });
    }

	AdminInit() {
		this.appSvc.GetJudges(this.User.AuthToken, this.User.CurrentRole, this.Award.AwardID, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(judges => {
            for (let j of judges) {
                let potentialNomType = this.AllNomTypes.find(f => f.NomineeTypeID === j.NomineeTypeID);
                if (potentialNomType != null) {
                    j.NomineeTypeName = potentialNomType.ShortName;
                }
            }
            this.AllJudges = judges;
        });
    }

    ChooseJudge() {
        let dlg = this.matDialog.open<JudgeSelectDialogComponent>(JudgeSelectDialogComponent, {
            disableClose: true,
            data: this.AllJudges,
            width: '1000px'
        });

        dlg.afterClosed().pipe(takeUntil(this._subrelease)).subscribe(response => {
            if (response != null) {
                this.CurrentJudge = response.SortableName;
                this.JudgeInit(response.JudgeID);
            }
        });
    }

    ChangeTab(nomIndex: number, tabIndex: number) {
        this.Judge.Nominees[nomIndex].NominationForm.SelectedTabIndex = tabIndex;

        // Trigger the scroll event so the master container can scroll the panel back up to view.
        this.scrollSvc.TriggerScroll();
    }

    CreateRankingDetailFormGroup(nom: JudgeNominationInfoEntry): FormGroup {
        return this.fb.group({
            NomID: nom.NominationID,
            Rank: [nom.SelectedRanking, Validators.required]
        });
    }

    DuplicateExists(index: number): boolean {
        if (this.DuplicateRank != null) {
            if (this.TieForm.value.RankingsList[index].Rank === this.DuplicateRank) {
                return true;
            }
        }
        return false;
    }

    Finalize() {
        this.appSvc.FinalizeJudgeNominationGroup(
            this.User.AuthToken,
            this.User.CurrentRole,
            this.Judge.JudgeID
        ).pipe(takeUntil(this._subrelease)).subscribe(refresh => {
            this.Judge = refresh;
        });
    }

    Rate(nom: JudgeNominationInfoEntry) {
        this.router.navigate([this.Award.URLPath + '/judgeform', nom.NominationFormUID, nom.JudgeID, nom.JudgingPhase]);
    }

    Submit(form: FormGroup) {
        if (form.invalid || this.DuplicateRank != null) {
            return;
        }

        for (let n of form.value.RankingsList) {
            this.Judge.Nominees.find(f => f.NominationID === n.NomID).SelectedRanking = n.Rank;
        }

        this.appSvc.FinalizeJudgeNominationTieBreakerGroup(
            this.User.AuthToken,
            this.User.CurrentRole,
            this.Judge.JudgeID,
            this.Judge.Nominees
        ).pipe(takeUntil(this._subrelease)).subscribe(refresh => {
            this.Judge = refresh;
        });
    }

    ViewForm(index: number) {
        this.Judge.Nominees[index].NominationForm.ShowingNominationForm = !this.Judge.Nominees[index].NominationForm.ShowingNominationForm;
    }
}
